import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminPanel.css";
import AdminSidebar from "./AdminSidebar";
import AdministratorSidebar from "./AdministratorSidebar";
import UserSidebar from "./UserSidebar";

const API_BASE_URL = "https://skcapi.btcjapan.co/sales_api.php";

const AddSale = () => {
    const navigate = useNavigate();
    const [userType, setUserType] = useState("");
    const [userFullName, setUserFullName] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [availableStockOptions, setAvailableStockOptions] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedVehicles, setSelectedVehicles] = useState([]);
    const [saleData, setSaleData] = useState({});
    const today = new Date().toISOString().split("T")[0];

    useEffect(() => {
        const storedUserType = localStorage.getItem("usertype");
        const storedUserName = localStorage.getItem("userName");
        const storedFName = localStorage.getItem("fname");
        const storedLName = localStorage.getItem("lname");

        if (!storedUserType) {
            navigate("/login");
        } else {
            setUserType(storedUserType);
            if (storedFName && storedLName) {
                setUserFullName(`${storedFName} ${storedLName}`);
            } else if (storedFName) {
                setUserFullName(storedFName);
            } else {
                setUserFullName(storedUserName);
            }
        }

        fetchAvailableStockOptions();
        fetchUsers();
    }, [navigate]);

    const fetchAvailableStockOptions = async () => {
        try {
            setLoading(true);
            const [carDetailsResponse, salesResponse] = await Promise.all([
                fetch(`${API_BASE_URL}?action=get_car_details`),
                fetch(`${API_BASE_URL}?action=get_sales`),
            ]);

            const [carDetailsData, salesData] = await Promise.all([
                carDetailsResponse.json(),
                salesResponse.json(),
            ]);

            if (!carDetailsResponse.ok || !carDetailsData.success) {
                throw new Error(carDetailsData.message || "Failed to fetch car details");
            }

            if (!salesResponse.ok || !salesData.success) {
                throw new Error(salesData.message || "Failed to fetch sales");
            }

            const soldStockIds = salesData.data.map((sale) => sale.stock_id);
            const availableCars = carDetailsData.data.filter(
                (car) => !soldStockIds.includes(car.stock_id)
            );

            setAvailableStockOptions(availableCars);
            const initialSaleData = availableCars.reduce((acc, car) => {
                acc[car.stock_id] = {
                    date: today,
                    client_id: "",
                    sold_price: car.price || "" // Default to car.price
                };
                return acc;
            }, {});
            setSaleData(initialSaleData);
        } catch (error) {
            console.error("Error fetching available stock options:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}?action=get_users`);
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || "Failed to fetch users");
            }

            setUsers(data.data || []);
        } catch (error) {
            console.error("Error fetching users:", error);
            setError(error.message);
        }
    };

    const handleCheckboxChange = (stockId) => {
        setSelectedVehicles((prev) =>
            prev.includes(stockId)
                ? prev.filter((id) => id !== stockId)
                : [...prev, stockId]
        );
    };

    const handleInputChange = (stockId, field, value) => {
        setSaleData((prev) => ({
            ...prev,
            [stockId]: { ...prev[stockId], [field]: value },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            const salesToAdd = selectedVehicles.map((stockId) => {
                const client = users.find(
                    (user) => user.id.toString() === saleData[stockId].client_id
                );
                return {
                    client_id: saleData[stockId].client_id,
                    client_name: client ? `${client.fname} ${client.lname}` : "",
                    stock_id: stockId,
                    sale_date: saleData[stockId].date,
                    sold_by: localStorage.getItem("userName") || "admin",
                    sold_price: saleData[stockId].sold_price || null,
                };
            });

            if (salesToAdd.length === 0) {
                throw new Error("No vehicles selected for sale");
            }

            const invalidSales = salesToAdd.filter(
                (sale) => !sale.client_id || !sale.client_name
            );
            if (invalidSales.length > 0) {
                throw new Error("Please select a client for all selected vehicles");
            }

            const invalidPrices = salesToAdd.filter(
                (sale) => !sale.sold_price || sale.sold_price <= 0
            );
            if (invalidPrices.length > 0) {
                throw new Error("Please enter a valid price for all selected vehicles");
            }

            for (const sale of salesToAdd) {
                // Update sold_price in car_details
                const priceResponse = await fetch(`${API_BASE_URL}?action=update_sold_price`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        stock_id: sale.stock_id,
                        sold_price: sale.sold_price,
                    }),
                });

                const priceData = await priceResponse.json();
                if (!priceResponse.ok || !priceData.success) {
                    throw new Error(priceData.message || "Failed to update sold price");
                }

                // Record the sale
                const saleResponse = await fetch(`${API_BASE_URL}?action=add_sale`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(sale),
                });

                const saleDataResponse = await saleResponse.json();
                if (!saleResponse.ok || !saleDataResponse.success) {
                    throw new Error(saleDataResponse.message || "Failed to add sale");
                }
            }

            setSuccess("Sales added successfully!");
            setSelectedVehicles([]);
            setSaleData({});
            await fetchAvailableStockOptions();
        } catch (error) {
            console.error("Error adding sales:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("email");
        localStorage.removeItem("userName");
        localStorage.removeItem("usertype");
        localStorage.removeItem("fname");
        localStorage.removeItem("lname");
        navigate("/login");
    };

    return (
        <div className="admin-container">
            <div className="sidebar-container">
                {userType === "administrator" && <AdministratorSidebar />}
                {userType === "admin" && <AdminSidebar />}
                {userType === "user" && <UserSidebar />}
            </div>

            <div className="admin-content">
                <div className="admin-navbar">
                    <h1 className="welcome-text">Welcome to the Admin Panel, {userFullName}</h1>
                    <button onClick={logout} className="logout-btn btn btn-warning">
                        Logout
                    </button>
                </div>

                <div className="mainsection">
                    <h3 className='section-title text-center'>View All User</h3>

                    {error && <div className="alert alert-danger">{error}</div>}
                    {success && <div className="alert alert-success">{success}</div>}
                    <button
                        onClick={handleSubmit}
                        className="btn btn-success mb-3"
                        disabled={loading || selectedVehicles.length === 0}
                    >
                        {loading ? "Processing..." : "Add Sale"}
                    </button>
                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Date</th>
                                <th>Client Name</th>
                                <th>VIN</th>
                                <th>Stock ID</th>
                                <th>Maker</th>
                                <th>Year</th>
                                <th>Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            {availableStockOptions.map((car) => (
                                <tr key={car.stock_id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedVehicles.includes(car.stock_id)}
                                            onChange={() => handleCheckboxChange(car.stock_id)}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={saleData[car.stock_id]?.date || today}
                                            onChange={(e) =>
                                                handleInputChange(car.stock_id, "date", e.target.value)
                                            }
                                        />
                                    </td>
                                    <td>
                                        <select
                                            className="form-control"
                                            value={saleData[car.stock_id]?.client_id || ""}
                                            onChange={(e) =>
                                                handleInputChange(
                                                    car.stock_id,
                                                    "client_id",
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="">Select Client</option>
                                            {users.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.fname} {user.lname} ({user.username})
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>{car.vin}</td>
                                    <td>{car.stock_id}</td>
                                    <td>{car.maker}</td>
                                    <td>{car.year}</td>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={saleData[car.stock_id]?.sold_price || ""}
                                            onChange={(e) =>
                                                handleInputChange(car.stock_id, "sold_price", e.target.value)
                                            }
                                            min="0"
                                            step="0.01"
                                            placeholder="Enter price"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {availableStockOptions.length === 0 && !loading && (
                        <div className="text-danger">No available vehicles found</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddSale;